/* Los fichajes del puerto, agrupados por persona y por día.
 *
 * EL GEMELO ESTÁ EN FLOTABOT (`fichaje.agrupar` / `fichaje.semana_de`) Y TIENE
 * QUE DECIR LO MISMO. Aquél arma el mail de los lunes y éste la pantalla; si se
 * separan, el mail dice que alguien entró el martes y la pantalla no lo muestra
 * — y ahí no se sabe a cuál creerle. Hay una tabla de casos idéntica en
 * `test_fichaje.py` y en `puerto.test.js`.
 *
 * ── LA FOTO NO ES UN FICHAJE: ES UNA FOTO DE UN RELOJ ───────────────────────
 *
 * De ahí sale todo lo que sigue. El sistema puede afirmar **«a las 09:12 alguien
 * mandó una foto de un molinete que marcaba 09:07»**; no puede afirmar «entró a
 * las 09:07». Por eso las dos horas van juntas y etiquetadas, y ninguna se hace
 * pasar por la otra.
 *
 * ── UNA ENTRADA SIN SALIDA NO SE COMPLETA ───────────────────────────────────
 *
 * Se muestra el hueco. Inventar la salida convierte *un dato que falta*
 * —visible, corregible— en *una jornada de nueve horas*, creíble y falsa. Es la
 * lección de la geocerca de destino.
 *
 * ── Y LOS DE MÁS TAMPOCO SE DESCARTAN ───────────────────────────────────────
 *
 * Tres entradas el mismo día es justo lo que hay que mirar; quedarse con la
 * primera lo escondería.
 */

export const ENTRADA = "entrada";
export const SALIDA = "salida";

/* ── LA HORA SE LEE EN UTC-3 FIJO Y NO EN LA DEL NAVEGADOR ──────────────────
 *
 * Lo encontró un test, y no es un detalle del entorno de pruebas. El mail de
 * los lunes formatea en UTC-3 fijo (`molinete_foto.TZ_AR`), así que con la hora
 * local del navegador el MISMO fichaje diría 09:12 en el mail y 13:12 para
 * alguien que abra el hub desde otro huso — y no habría forma de saber cuál es
 * la real. Con un dato que se usa para hablar del horario de una persona, eso
 * no puede pasar.
 *
 * Fijo y no `America/Argentina/Buenos_Aires` por la misma razón que los crones:
 * hoy dan lo mismo, y con la zona nombrada un cambio de huso movería horarios
 * sin que nadie tocara nada.
 */
const TZ_AR_MS = -3 * 3600 * 1000;

/* El mismo instante, corrido para poder leerlo con `getUTC*` como si fuera hora
 * argentina. NO cambia el instante que se guardó: cambia cómo se lee. */
const enAR = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getTime() + TZ_AR_MS);
};

const p2 = (n) => String(n).padStart(2, "0");

const hhmm = (iso) => {
  const d = enAR(iso);
  if (!d) return null;
  return `${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}`;
};

const dia = (iso) => {
  const d = enAR(iso);
  if (!d) return null;
  return `${d.getUTCFullYear()}-${p2(d.getUTCMonth() + 1)}-${p2(d.getUTCDate())}`;
};

/* El lunes 00:00 de la semana ARGENTINA que contiene ese instante.
 *
 * Se calcula sobre el día argentino y no sobre el del navegador: a las 22:00 de
 * un domingo en Buenos Aires ya es lunes en UTC, y el corte caería una semana
 * adelante para quien esté mirando.
 */
function lunesAR(cuando) {
  const d = enAR(cuando instanceof Date ? cuando.toISOString() : cuando);
  if (!d) return null;
  // `getUTCDay()` da 0 el domingo; el lunes es el día 1 de la semana.
  const alLunes = (d.getUTCDay() + 6) % 7;
  const dd = d.getUTCDate() - alLunes;
  // Vuelve a ser un instante de verdad: 00:00 en Argentina es 03:00 UTC.
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), dd, 0, 0, 0) - TZ_AR_MS,
  );
}

export function agrupar(filas) {
  const porPersona = new Map();

  for (const f of filas || []) {
    const num = String(f?.numero || "");
    const fecha = dia(f?.recibido_en);
    if (!fecha) continue;

    if (!porPersona.has(num)) {
      porPersona.set(num, {
        numero: num,
        persona: f.persona || num,
        rol: f.rol || "",
        dias: new Map(),
      });
    }
    const p = porPersona.get(num);
    // El nombre congelado más reciente gana: si alguien cambió de apellido a
    // mitad del período, la pantalla lo nombra como se lo nombra hoy.
    if (f.persona) p.persona = f.persona;

    if (!p.dias.has(fecha)) {
      p.dias.set(fecha, { fecha, entrada: null, salida: null, sueltos: [] });
    }
    const d = p.dias.get(fecha);
    const marca = {
      id: f.id,
      hora: hhmm(f.recibido_en),
      /* `null` y no la hora del mensaje: son dos cosas distintas y la pantalla
       * dice «sin hora del molinete» donde no la hay. Rellenarla haría que un
       * fichaje con foto legible y uno sin ella se lean igual. */
      foto: hhmm(f.hora_foto),
      archivo: f.foto || null,
      dudas: f.dudas || [],
      tipo: f.tipo,
    };
    if (d[f.tipo] === null && (f.tipo === ENTRADA || f.tipo === SALIDA)) {
      d[f.tipo] = marca;
    } else {
      d.sueltos.push(marca);
    }
  }

  return [...porPersona.values()]
    .map((p) => ({
      ...p,
      dias: [...p.dias.values()].sort((a, b) => (a.fecha < b.fecha ? 1 : -1)),
    }))
    // Por nombre, que es como se lee una lista de gente.
    .sort((a, b) => a.persona.localeCompare(b.persona, "es"));
}

/* La semana que TERMINÓ: lunes a lunes.
 *
 * Es la misma que mira el mail de los lunes, y por eso no incluye lo que va del
 * lunes de hoy: mezclar un día suelto con una semana cerrada haría que ese
 * lunes se cuente dos veces, en este período y en el que viene.
 */
export function semanaDe(hoy = new Date()) {
  const lunes = lunesAR(hoy);
  return { desde: new Date(lunes.getTime() - 7 * 86400000), hasta: lunes };
}

/* Y la semana EN CURSO, que es la que la pantalla abre por defecto.
 *
 * Acá se separa del mail a propósito, y es la única diferencia entre los dos:
 * el parte del lunes cierra la semana anterior porque es un informe, y la
 * pantalla se abre para ver **qué pasó hoy**. Abrirla en la semana cerrada
 * mostraría un registro viejo justo cuando alguien entra a comprobar que el
 * fichaje de esta mañana quedó.
 */
export function semanaEnCurso(hoy = new Date()) {
  const lunes = lunesAR(hoy);
  return { desde: lunes, hasta: new Date(lunes.getTime() + 7 * 86400000) };
}

/* El día argentino de un instante, `AAAA-MM-DD`. Lo usa la pantalla para
 * rotular el rango, y tiene que ser el MISMO que agrupa las filas. */
export const diaAR = dia;

export const iso = (d) => d.toISOString();

export const fechaCorta = (f) => {
  const [a, m, d] = String(f || "").split("-");
  if (!a || !m || !d) return f;
  return `${d}/${m}`;
};

/* Cuántos días tienen un hueco: entrada sin salida, o salida sin entrada.
 *
 * Va arriba de la tabla porque es lo único accionable de esta pantalla. Y se
 * CUENTA en vez de esconderse: un registro incompleto no es un error del
 * sistema, es alguien que se olvidó de fichar — y eso se arregla hablando con
 * esa persona, no mirando la pantalla otra vez.
 */
export function huecos(gente) {
  let n = 0;
  for (const p of gente || []) {
    for (const d of p.dias || []) {
      if (!d.entrada || !d.salida) n += 1;
    }
  }
  return n;
}

/* ── CUÁNTO TRABAJÓ, EN HORAS Y MINUTOS (pedido de Nico, 09/09) ──────────────
 *
 * Del par entrada/salida del día. Se calcula sobre la HORA DEL MENSAJE
 * (`recibido_en`, la que el servidor sabe y no se puede falsear — la misma que
 * la pantalla ya muestra grande), no sobre la foto del molinete, que falta
 * seguido y se puede trucar.
 *
 * UN DÍA SIN ENTRADA O SIN SALIDA NO SUMA. Es un hueco —ya se cuenta arriba—,
 * no una jornada de cero: inventarle un total sería la misma mentira que
 * inventarle la salida. Y una salida ANTERIOR a la entrada tampoco se cuenta:
 * es un dato raro (fichó al revés), no una jornada negativa.
 */
const _min = (hhmm) => {
  const [h, m] = String(hhmm || "").split(":");
  const H = Number(h);
  const M = Number(m);
  return Number.isInteger(H) && Number.isInteger(M) ? H * 60 + M : null;
};

export function minutosDia(d) {
  const e = _min(d?.entrada?.hora);
  const s = _min(d?.salida?.hora);
  if (e === null || s === null) return null;
  const diff = s - e;
  return diff >= 0 ? diff : null;
}

export function duracionTexto(min) {
  if (min === null || min === undefined) return "—";
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h && m) return `${h}h ${p2(m)}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

/* El acumulado de la semana de una persona: la suma de los días completos.
 * `null` si no hubo ningún día con entrada y salida —así la pantalla muestra
 * «—» en vez de «0m», que se leería como que trabajó cero. */
export function totalSemanaMin(persona) {
  let total = 0;
  let hubo = false;
  for (const d of persona?.dias || []) {
    const mm = minutosDia(d);
    if (mm !== null) {
      total += mm;
      hubo = true;
    }
  }
  return hubo ? total : null;
}

/* El día de la semana de una fecha argentina `AAAA-MM-DD`, abreviado. Se lee en
 * UTC sobre la fecha ya-argentina (no un instante), así que no hay corrimiento
 * de huso: el día es el que agrupó la fila. */
const _DIAS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
export function diaSemana(fecha) {
  const [a, m, d] = String(fecha || "").split("-").map(Number);
  if (!a || !m || !d) return "";
  return _DIAS[new Date(Date.UTC(a, m - 1, d)).getUTCDay()] || "";
}
